// STOMP WebSocket singleton for real-time chat.
//
// Deliberately framework-free (no React, no queryClient imports) so it can be
// unit-tested in isolation. React integrates through ChatSocketProvider, which
// routes parsed events into the React Query cache.
//
// Delivery model: best-effort online-only. REST is the source of truth; on
// reconnect the provider re-fetches so any gap is filled from the API.

import { Client, type StompSubscription } from "@stomp/stompjs";
import { env } from "@/config/env";
import type { InboxEvent, ThreadEvent } from "../types";

export type ChatSocketStatus = "disconnected" | "connecting" | "connected";

type ThreadListener = (event: ThreadEvent) => void;
type InboxListener = (event: InboxEvent) => void;
type StatusListener = (status: ChatSocketStatus) => void;

const RECONNECT_DELAY_MS = 5000;
const HEARTBEAT_MS = 10_000;

class ChatSocket {
  private client: Client | null = null;
  private status: ChatSocketStatus = "disconnected";

  private threadListeners = new Map<string, Set<ThreadListener>>();
  private inboxListeners = new Set<InboxListener>();
  private hrQueueListeners = new Set<InboxListener>();
  private statusListeners = new Set<StatusListener>();
  private subscriptions = new Map<string, StompSubscription>();
  private inboxSubscription: StompSubscription | null = null;
  private hrQueueSubscription: StompSubscription | null = null;
  /** Fired after every (re)connect — the provider uses it to refetch */
  private reconnectListeners = new Set<() => void>();
  private hasConnectedOnce = false;

  getStatus(): ChatSocketStatus {
    return this.status;
  }

  connect(token: string): void {
    if (this.client?.active) return;

    this.setStatus("connecting");

    this.client = new Client({
      brokerURL: env.VITE_WS_URL,
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: RECONNECT_DELAY_MS,
      heartbeatIncoming: HEARTBEAT_MS,
      heartbeatOutgoing: HEARTBEAT_MS,
      onConnect: () => {
        this.setStatus("connected");
        this.resubscribeAll();
        if (this.hasConnectedOnce) {
          // Gap-fill: notify the React layer to refetch from REST
          this.reconnectListeners.forEach((listener) => {
            listener();
          });
        }
        this.hasConnectedOnce = true;
      },
      onStompError: () => {
        this.setStatus("disconnected");
      },
      onWebSocketClose: () => {
        this.setStatus(this.client?.active ? "connecting" : "disconnected");
      },
    });

    this.client.activate();
  }

  disconnect(): void {
    this.subscriptions.clear();
    this.inboxSubscription = null;
    this.hasConnectedOnce = false;
    void this.client?.deactivate();
    this.client = null;
    this.setStatus("disconnected");
  }

  /** Subscribe to a thread's live events. Returns an unsubscribe function. */
  subscribeThread(threadId: string, listener: ThreadListener): () => void {
    let listeners = this.threadListeners.get(threadId);
    if (!listeners) {
      listeners = new Set();
      this.threadListeners.set(threadId, listeners);
    }
    listeners.add(listener);

    if (this.isConnected() && !this.subscriptions.has(threadId)) {
      this.openThreadSubscription(threadId);
    }

    return () => {
      const set = this.threadListeners.get(threadId);
      set?.delete(listener);
      if (set && set.size === 0) {
        this.threadListeners.delete(threadId);
        this.subscriptions.get(threadId)?.unsubscribe();
        this.subscriptions.delete(threadId);
      }
    };
  }

  /** Subscribe to the per-user inbox queue. Returns an unsubscribe function. */
  subscribeInbox(listener: InboxListener): () => void {
    this.inboxListeners.add(listener);

    if (this.isConnected() && !this.inboxSubscription) {
      this.openInboxSubscription();
    }

    return () => {
      this.inboxListeners.delete(listener);
      if (this.inboxListeners.size === 0) {
        this.inboxSubscription?.unsubscribe();
        this.inboxSubscription = null;
      }
    };
  }

  /** Subscribe to the shared HR queue channel (/topic/hr-queue). Returns an unsubscribe function. */
  subscribeHrQueue(listener: InboxListener): () => void {
    this.hrQueueListeners.add(listener);

    if (this.isConnected() && !this.hrQueueSubscription) {
      this.openHrQueueSubscription();
    }

    return () => {
      this.hrQueueListeners.delete(listener);
      if (this.hrQueueListeners.size === 0) {
        this.hrQueueSubscription?.unsubscribe();
        this.hrQueueSubscription = null;
      }
    };
  }

  onReconnect(listener: () => void): () => void {
    this.reconnectListeners.add(listener);
    return () => {
      this.reconnectListeners.delete(listener);
    };
  }

  onStatusChange(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  private isConnected(): boolean {
    return this.status === "connected" && !!this.client;
  }

  private setStatus(status: ChatSocketStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((listener) => {
      listener(status);
    });
  }

  /** Re-establish every active subscription after a (re)connect */
  private resubscribeAll(): void {
    this.subscriptions.clear();
    this.inboxSubscription = null;
    this.hrQueueSubscription = null;
    for (const threadId of this.threadListeners.keys()) {
      this.openThreadSubscription(threadId);
    }
    if (this.inboxListeners.size > 0) {
      this.openInboxSubscription();
    }
    if (this.hrQueueListeners.size > 0) {
      this.openHrQueueSubscription();
    }
  }

  private openThreadSubscription(threadId: string): void {
    if (!this.client) return;
    const subscription = this.client.subscribe(`/topic/threads/${threadId}`, (frame) => {
      const event = JSON.parse(frame.body) as ThreadEvent;
      this.threadListeners.get(threadId)?.forEach((listener) => {
        listener(event);
      });
    });
    this.subscriptions.set(threadId, subscription);
  }

  private openInboxSubscription(): void {
    if (!this.client) return;
    this.inboxSubscription = this.client.subscribe("/user/queue/inbox", (frame) => {
      const event = JSON.parse(frame.body) as InboxEvent;
      this.inboxListeners.forEach((listener) => {
        listener(event);
      });
    });
  }

  private openHrQueueSubscription(): void {
    if (!this.client) return;
    this.hrQueueSubscription = this.client.subscribe("/topic/hr-queue", (frame) => {
      const event = JSON.parse(frame.body) as InboxEvent;
      this.hrQueueListeners.forEach((listener) => {
        listener(event);
      });
    });
  }
}

export const chatSocket = new ChatSocket();
